import type { GeoJsonCollection } from './features-buffer-geom-translator';

// ============================================================================
// Enums
// ============================================================================

/* eslint-disable @typescript-eslint/naming-convention -- API operator enum values are uppercase with underscores */
export enum Op {
  EQ = 'EQ',
  IN = 'IN',
  OVERLAPS = 'OVERLAPS',
  NOTEXISTS = 'NOTEXISTS',
  EXISTS = 'EXISTS',
  LT = 'LT',
  LTEQ = 'LTEQ',
  GT = 'GT',
  GTEQ = 'GTEQ',
  NE = 'NE',
  LIKE = 'LIKE',
  CONTAINED_IN = 'CONTAINED_IN',
  NOTIN = 'NOTIN',
  NOTCONTAINED_IN = 'NOTCONTAINED_IN',
  NOTLIKE = 'NOTLIKE',
}
/* eslint-enable @typescript-eslint/naming-convention */

/* eslint-disable no-magic-numbers -- HTTP status codes are standard values */
export enum HttpStatus {
  Ok = 200,
  NoContent = 204,
}
/* eslint-enable no-magic-numbers */

// ============================================================================
// Types
// ============================================================================

interface FilterClause {
  operator: Op;
  field: string;
  operand: string | boolean | string[];
}

interface FilterRequest {
  or: FilterClause[];
}

type FilterExpression = FilterClause | FilterRequest;

const COMMON_LAYER_ID_FIELD = 'metadata.common_layerId';
const COMMON_JOB_ID_FIELD = 'metadata.common_jobId';

// ============================================================================
// Public Utility Functions
// ============================================================================

/**
 * Gets current timestamp without the trailing 'Z'
 * The API expects ISO timestamp without timezone indicator
 */
export function getTimestamp(): string {
  return new Date().toISOString().slice(0, -1);
}

/**
 * Validates that the response is a valid GeoJsonCollection
 * @param data - Unknown data to validate
 * @returns Type guard confirming data is a valid GeoJsonCollection
 */
export function validateGeoJsonResponse(
  data: unknown
): data is GeoJsonCollection {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'FeatureCollection' &&
    'features' in data &&
    Array.isArray((data as GeoJsonCollection).features)
  );
}

/**
 * Creates a filter clause for the Features Service API.
 * Layer and job constraints are AND-ed together. Multiple layer IDs use OR
 * with EQ operands; multiple job IDs use an OR wrapper with a single IN operand
 * so visibility can change dynamically without rebuilding the fetcher.
 * @param layerIds - Array of layer IDs to filter by
 * @param jobIds - Optional survey job IDs for field-mission tile filtering
 * @returns JSON string representation of the filter clause
 * @example
 * createFilterClause(['layer1'])
 * // Returns: '[{"operator":"EQ","field":"metadata.common_layerId","operand":"layer1"}]'
 *
 * createFilterClause(['layer1', 'layer2'])
 * // Returns: '[{"or":[{"operator":"EQ","field":"metadata.common_layerId","operand":"layer1"},{"operator":"EQ","field":"metadata.common_layerId","operand":"layer2"}]}]'
 *
 * createFilterClause(['layer1'], ['job1'])
 * // Returns: '[{"operator":"EQ","field":"metadata.common_layerId","operand":"layer1"},{"operator":"EQ","field":"metadata.common_jobId","operand":"job1"}]'
 *
 * createFilterClause(['layer1'], ['job1', 'job2'])
 * // Returns: '[{"operator":"EQ","field":"metadata.common_layerId","operand":"layer1"},{"or":[{"operator":"IN","field":"metadata.common_jobId","operand":["job1","job2"]}]}]'
 */
export function createFilterClause(
  layerIds: readonly string[],
  jobIds?: readonly string[]
): string {
  const clauses: FilterExpression[] = [];

  const layerFilter = createLayerIdFilter(layerIds);
  if (layerFilter) {
    clauses.push(layerFilter);
  }

  const jobFilter = createJobIdFilter(jobIds ?? []);
  if (jobFilter) {
    clauses.push(jobFilter);
  }

  return JSON.stringify(clauses);
}

// ============================================================================
// Private Helper Functions
// ============================================================================

function createLayerIdFilter(ids: readonly string[]): FilterExpression | null {
  if (ids.length === 0) {
    return null;
  }

  if (ids.length === 1) {
    return createFilter(Op.EQ, COMMON_LAYER_ID_FIELD, ids[0]);
  }

  return {
    or: ids.map((id) => createFilter(Op.EQ, COMMON_LAYER_ID_FIELD, id)),
  };
}

function createJobIdFilter(ids: readonly string[]): FilterExpression | null {
  if (ids.length === 0) {
    return null;
  }

  if (ids.length === 1) {
    return createFilter(Op.EQ, COMMON_JOB_ID_FIELD, ids[0]);
  }

  return {
    or: [createFilter(Op.IN, COMMON_JOB_ID_FIELD, [...ids])],
  };
}

/**
 * Normalizes single-element arrays to their scalar value
 */
function buildSingleOrArrayValue(
  value: string | boolean | string[]
): string | boolean | string[] {
  return Array.isArray(value) && value.length === 1 ? value[0] : value;
}

/**
 * Creates a filter clause object for the Features Service API
 */
function createFilter(
  operator: Op,
  field: string,
  value: string | boolean | string[]
): FilterClause {
  // Convert EQ to IN operator if there are multiple values
  const normalizedOperator =
    operator === Op.EQ && Array.isArray(value) && value.length > 1
      ? Op.IN
      : operator;

  return {
    operator: normalizedOperator,
    field,
    operand: buildSingleOrArrayValue(value),
  };
}
